import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faPenToSquare, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { API_CONFIG, canEditWorkOrder } from '../config/api';

const WorkOrderDetails = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [savingCheckmark, setSavingCheckmark] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
  const canEditCheckmark = currentUser?.role === 'admin' || currentUser?.role === 'finance';

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Nedostaje token. Prijavite se ponovno.');
        }
        const res = await fetch(`${API_CONFIG.ENDPOINTS.NALOZI}/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) {
          throw new Error('Ne mogu dohvatiti detalje naloga.');
        }
        const json = await res.json();
        setData(json);
        
        // Check if user can edit this work order
        const editPermission = await canEditWorkOrder(json.user_id || json.User?.id);
        setCanEdit(editPermission);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  const handleDownloadReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.ENDPOINTS.NALOZI}/report/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Neuspješno preuzimanje izvještaja.');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nalog-za-rad-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Greška pri preuzimanju: ${err.message}`);
    }
  };

  const toggleCheckmark = async () => {
    if (!data) return;
    const next = data.checkmark === 'Yes' ? 'No' : 'Yes';
    setSavingCheckmark(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_CONFIG.ENDPOINTS.NALOZI}/${id}/checkmark`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ checkmark: next })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Greška pri promjeni statusa.');
      setData(prev => ({ ...prev, checkmark: json.checkmark }));
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingCheckmark(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] text-gray-600">Učitavanje detalja...</div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">{error}</div>
        <Link to="/dashboard" className="text-blue-600 hover:underline">Natrag na dashboard</Link>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900">Nalog #{data.id_nalog}</h2>
        <div className="flex gap-2">
          <button onClick={handleDownloadReport} className="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700" title="PDF izvještaj">
            <FontAwesomeIcon icon={faFilePdf} />
          </button>
          {canEdit && (
            <Link to={`/work-orders/${id}/edit`} className="px-3 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700" title="Uredi">
              <FontAwesomeIcon icon={faPenToSquare} />
            </Link>
          )}
          <Link to="/dashboard" className="px-3 py-2 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200" title="Natrag">
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>
        </div>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 rounded-lg divide-y divide-gray-200">
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">Datum</div>
            <div className="font-medium text-gray-900">{data.datum || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Serviser</div>
            <div className="font-medium text-gray-900">{data.User ? `${data.User.first_name} ${data.User.last_name}` : '-'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Ustanova</div>
            <div className="font-medium text-gray-900">{data.Ustanove?.naziv_ustanove || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Aparat</div>
            <div className="font-medium text-gray-900">{data.Aparati ? `${data.Aparati.naziv_aparata} (${data.Aparati.id_broj})` : '-'}</div>
          </div>
          <div className="sm:col-span-2">
            <div className="text-sm text-gray-500">Finansijski obrađeno</div>
            <div className="flex items-center gap-3 mt-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${data.checkmark === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {data.checkmark === 'Yes' ? 'Da' : 'Ne'}
              </span>
              {canEditCheckmark && (
                <button onClick={toggleCheckmark} disabled={savingCheckmark} className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                  {savingCheckmark ? 'Spremam...' : (data.checkmark === 'Yes' ? 'Odznači' : 'Označi')}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <div className="text-sm text-gray-500">Vrijeme početka</div>
            <div className="font-medium text-gray-900">{data.vrijeme_pocetka || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Vrijeme završetka</div>
            <div className="font-medium text-gray-900">{data.vrijeme_zavrsetka || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Ukupno radnih sati</div>
            <div className="font-medium text-gray-900">{data.ukupno_radnih_sati || '-'}</div>
          </div>
        </div>

        <div className="p-4">
          <div className="text-sm text-gray-500">Relacija</div>
          <div className="font-medium text-gray-900 whitespace-pre-wrap">{data.relacija || '-'}</div>
        </div>

        <div className="p-4">
          <div className="text-sm text-gray-500">Opis kvara/intervencije</div>
          <div className="font-medium text-gray-900 whitespace-pre-wrap">{data.opis_kvara_intervencija || '-'}</div>
        </div>

        <div className="p-4">
          <div className="text-sm text-gray-500">Opis poslova</div>
          <div className="font-medium text-gray-900 whitespace-pre-wrap">{data.opis_poslova || '-'}</div>
        </div>

        <div className="p-4">
          <div className="text-sm text-gray-500">Napomena</div>
          <div className="font-medium text-gray-900 whitespace-pre-wrap">{data.napomena || '-'}</div>
        </div>

        <div className="p-4">
          <div className="text-sm text-gray-500">Interna napomena</div>
          <div className="font-medium text-gray-900 whitespace-pre-wrap">{data.interna_napomena || '-'}</div>
        </div>

        <div className="p-4">
          <div className="text-sm text-gray-500 mb-2">Utrošeni dijelovi</div>
          {Array.isArray(data.utroseniDijelovi) && data.utroseniDijelovi.length > 0 ? (
            <div className="overflow-x-auto border border-gray-200 rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Naziv dijela</th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Količina</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.utroseniDijelovi.map((item) => (
                    <tr key={item.id_utroseni_dijelovi} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {item.Dijelovi?.naziv_dijela || `Dio #${item.id_dijelovi}`}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">{item.kolicina}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-gray-700">-</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkOrderDetails;
